import { useState, useEffect, useRef, useCallback } from "react";
import { useAuth } from "../../contexts/AuthContext";
import socket from "../../lib/socket";
import apiClient from "../../lib/api";
import {
  Headset, PaperPlaneRight, ShieldStar, User as UserIcon,
  ChatCircleDots, ArrowLeft,
} from "@phosphor-icons/react";
import { formatDistanceToNow } from "date-fns";
import { es } from "date-fns/locale";
import { useNotificationSound } from "../../lib/useNotificationSound";
import { BRAND } from "../../lib/brand";
import AdminSection from "../../components/admin/AdminSection";

function formatTime(iso) {
  try {
    return formatDistanceToNow(new Date(iso), { addSuffix: true, locale: es });
  } catch {
    return "";
  }
}

export default function AdminSupportPage() {
  const { user } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [selected, setSelected] = useState(null); // user_id of selected conv
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [connected, setConnected] = useState(false);
  const [notifications, setNotifications] = useState({}); // user_id → unread count
  const bottomRef = useRef(null);
  const inputRef = useRef(null);
  const selectedRef = useRef(null); // ref to avoid stale closure
  const playSound = useNotificationSound();

  const scrollToBottom = useCallback(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, []);

  // Total unread count across all conversations
  const totalUnread = Object.values(notifications).reduce((a, b) => a + b, 0);

  // Tab title with unread count
  useEffect(() => {
    const base = BRAND.supportTitle;
    if (totalUnread > 0) {
      document.title = `(${totalUnread}) ${base}`;
    } else {
      document.title = base;
    }
    return () => { document.title = BRAND.name; };
  }, [totalUnread]);

  // Load conversation list
  useEffect(() => {
    apiClient.get("/admin/support/conversations")
      .then((r) => setConversations(r.data || []))
      .catch(() => {});
  }, []);

  // Socket setup
  useEffect(() => {
    if (!socket.connected) socket.connect();

    const onConnect = () => setConnected(true);
    const onDisconnect = () => setConnected(false);

    const onHistory = ({ conversation, messages: msgs }) => {
      setMessages(msgs);
      setTimeout(scrollToBottom, 60);
    };

    const onMessage = (msg) => {
      setMessages((prev) => {
        if (prev.find((m) => m.id === msg.id)) return prev;
        return [...prev, msg];
      });
      // Update conversation list last message
      setConversations((prev) =>
        prev.map((c) =>
          c.user_id === msg.conversation_user_id
            ? { ...c, last_message_at: msg.created_at }
            : c
        )
      );
      // Sound + unread badge when from user (not the one currently open)
      if (msg.sender_role === "user") {
        if (msg.conversation_user_id !== selectedRef.current) {
          setNotifications((n) => ({
            ...n,
            [msg.conversation_user_id]: (n[msg.conversation_user_id] || 0) + 1,
          }));
        }
        playSound();
      }
      setTimeout(scrollToBottom, 60);
    };

    const onNewConversation = (conv) => {
      setConversations((prev) => {
        if (prev.find((c) => c.user_id === conv.user_id)) return prev;
        return [conv, ...prev];
      });
      playSound();
    };

    const onNotification = ({ user_id }) => {
      setNotifications((n) => ({
        ...n,
        [user_id]: (n[user_id] || 0) + 1,
      }));
    };

    socket.on("connect", onConnect);
    socket.on("disconnect", onDisconnect);
    socket.on("support:history", onHistory);
    socket.on("support:message", onMessage);
    socket.on("support:new_conversation", onNewConversation);
    socket.on("support:notification", onNotification);

    if (socket.connected) setConnected(true);

    return () => {
      socket.off("connect", onConnect);
      socket.off("disconnect", onDisconnect);
      socket.off("support:history", onHistory);
      socket.off("support:message", onMessage);
      socket.off("support:new_conversation", onNewConversation);
      socket.off("support:notification", onNotification);
    };
  }, [scrollToBottom, playSound]); // ← removed "selected" to avoid listener teardown on conv switch

  const openConversation = (userId) => {
    setSelected(userId);
    selectedRef.current = userId;
    setMessages([]);
    setNotifications((n) => ({ ...n, [userId]: 0 }));
    socket.emit("support:admin_join", { user_id: userId });
  };

  const sendReply = (e) => {
    e.preventDefault();
    const trimmed = text.trim();
    if (!trimmed || !selected || !connected) return;
    socket.emit("support:admin_reply", { conversation_user_id: selected, text: trimmed });
    setText("");
    inputRef.current?.focus();
  };

  const selectedConv = conversations.find((c) => c.user_id === selected);

  return (
    <div className="px-4 lg:px-6 py-6 max-w-6xl mx-auto">
      <style>{`
        @keyframes support-badge-pulse {
          0%, 100% { transform: scale(1); }
          50%       { transform: scale(1.15); }
        }
      `}</style>

      <AdminSection
        icon={Headset}
        title="Soporte — Conversaciones"
        subtitle="Mensajes privados de suscriptores"
        color="sky"
        action={
          totalUnread > 0 ? (
            <span
              data-testid="support-total-unread"
              className="ml-1 px-2 py-0.5 rounded-full text-xs font-black"
              style={{ background: "#EF4444", color: "#fff", animation: "support-badge-pulse 1.5s ease-out infinite" }}
            >
              {totalUnread}
            </span>
          ) : null
        }
      >
      <div
        className="flex rounded-2xl overflow-hidden"
        style={{
          border: "1px solid rgba(255,255,255,0.06)",
          height: "calc(100dvh - 230px)",
          minHeight: "400px",
          background: "#111113",
        }}
      >
        {/* Conversation list */}
        <div
          className={`flex-shrink-0 flex flex-col border-r ${selected ? "hidden lg:flex" : "flex"}`}
          style={{
            width: "280px",
            borderColor: "rgba(255,255,255,0.06)",
          }}
        >
          <div
            className="px-4 py-3 flex-shrink-0"
            style={{ borderBottom: "1px solid rgba(255,255,255,0.06)" }}
          >
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full" style={{ background: connected ? "#22C55E" : "#71717A" }} />
              <span className="text-xs font-semibold" style={{ color: connected ? "#22C55E" : "#52525B" }}>
                {connected ? "En línea" : "Conectando..."}
              </span>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto">
            {conversations.length === 0 && (
              <div className="flex flex-col items-center justify-center h-32 opacity-40">
                <ChatCircleDots className="w-8 h-8 text-zinc-600 mb-2" />
                <p className="text-xs text-zinc-600">Sin conversaciones aún</p>
              </div>
            )}
            {conversations.map((conv) => {
              const unread = notifications[conv.user_id] || 0;
              const isActive = selected === conv.user_id;
              return (
                <button
                  key={conv.user_id}
                  data-testid={`support-conv-${conv.user_id}`}
                  onClick={() => openConversation(conv.user_id)}
                  className="w-full flex items-center gap-3 px-4 py-3 text-left transition-all"
                  style={{
                    background: isActive ? "rgba(212,175,55,0.08)" : unread > 0 ? "rgba(239,68,68,0.04)" : "transparent",
                    borderLeft: isActive ? "2px solid #D4AF37" : unread > 0 ? "2px solid rgba(239,68,68,0.5)" : "2px solid transparent",
                  }}
                >
                  <div
                    className="w-9 h-9 rounded-full flex items-center justify-center text-sm font-black flex-shrink-0 relative"
                    style={{ background: "rgba(255,255,255,0.08)", color: "#fff" }}
                  >
                    {(conv.user_name || "?").charAt(0).toUpperCase()}
                    {unread > 0 && (
                      <span
                        className="absolute -top-0.5 -right-0.5 w-3 h-3 rounded-full"
                        style={{ background: "#EF4444", border: "2px solid #111113" }}
                      />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <span className={`text-sm truncate ${unread > 0 ? "font-bold text-white" : "font-semibold text-white"}`}>{conv.user_name}</span>
                      {unread > 0 && (
                        <span
                          data-testid={`support-unread-${conv.user_id}`}
                          className="w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-black flex-shrink-0"
                          style={{ background: "#EF4444", color: "#fff" }}
                        >
                          {unread}
                        </span>
                      )}
                    </div>
                    <span className="text-[10px] text-zinc-500 truncate block">{conv.user_email}</span>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Chat area */}
        <div className={`flex flex-col flex-1 ${!selected ? "hidden lg:flex" : "flex"}`}>
          {!selected ? (
            <div className="flex flex-col items-center justify-center h-full opacity-40">
              <ChatCircleDots className="w-12 h-12 text-zinc-600 mb-3" />
              <p className="text-sm text-zinc-500">Selecciona una conversación</p>
            </div>
          ) : (
            <>
              {/* Chat header */}
              <div
                className="flex items-center gap-3 px-4 py-3 flex-shrink-0"
                style={{ borderBottom: "1px solid rgba(255,255,255,0.06)" }}
              >
                <button
                  className="lg:hidden w-8 h-8 rounded-lg flex items-center justify-center"
                  style={{ background: "rgba(255,255,255,0.06)" }}
                  onClick={() => setSelected(null)}
                >
                  <ArrowLeft className="w-4 h-4 text-white" />
                </button>
                <div
                  className="w-9 h-9 rounded-full flex items-center justify-center text-sm font-black flex-shrink-0"
                  style={{ background: "rgba(255,255,255,0.1)", color: "#fff" }}
                >
                  {(selectedConv?.user_name || "?").charAt(0).toUpperCase()}
                </div>
                <div>
                  <div className="text-sm font-bold text-white">{selectedConv?.user_name}</div>
                  <div className="text-[10px] text-zinc-500">{selectedConv?.user_email}</div>
                </div>
              </div>

              {/* Messages */}
              <div className="flex-1 overflow-y-auto px-4 py-4" style={{ minHeight: 0 }}>
                {messages.length === 0 && (
                  <div className="flex items-center justify-center h-32 opacity-40">
                    <p className="text-xs text-zinc-600">Sin mensajes aún</p>
                  </div>
                )}
                {messages.map((msg) => {
                  const isAdmin = msg.sender_role === "admin";
                  return (
                    <div
                      key={msg.id}
                      data-testid="support-admin-message"
                      className={`flex gap-2 mb-3 ${isAdmin ? "flex-row-reverse" : ""}`}
                    >
                      <div
                        className="w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-black flex-shrink-0"
                        style={{
                          background: isAdmin ? "linear-gradient(135deg,#D4AF37,#A07C20)" : "rgba(255,255,255,0.1)",
                          color: isAdmin ? "#000" : "#fff",
                        }}
                      >
                        {isAdmin ? <ShieldStar className="w-3.5 h-3.5" weight="fill" /> : <UserIcon className="w-3.5 h-3.5" />}
                      </div>
                      <div className={`flex flex-col max-w-[75%] ${isAdmin ? "items-end" : "items-start"}`}>
                        <div
                          className="px-3 py-2 rounded-xl text-sm leading-relaxed"
                          style={isAdmin ? {
                            background: "rgba(212,175,55,0.1)",
                            border: "1px solid rgba(212,175,55,0.2)",
                            color: "#fff",
                            borderRadius: "12px 4px 12px 12px",
                          } : {
                            background: "rgba(255,255,255,0.07)",
                            color: "rgba(255,255,255,0.9)",
                            borderRadius: "4px 12px 12px 12px",
                          }}
                        >
                          {msg.text}
                        </div>
                        <span className="text-[9px] mt-1 px-1" style={{ color: "rgba(255,255,255,0.3)" }}>
                          {formatTime(msg.created_at)}
                        </span>
                      </div>
                    </div>
                  );
                })}
                <div ref={bottomRef} />
              </div>

              {/* Reply input */}
              <div
                className="flex-shrink-0 px-4 pb-4"
                style={{ borderTop: "1px solid rgba(255,255,255,0.06)", paddingTop: "12px" }}
              >
                <form onSubmit={sendReply} className="flex gap-2">
                  <input
                    ref={inputRef}
                    data-testid="support-admin-input"
                    type="text"
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    maxLength={2000}
                    placeholder="Responder como Admin..."
                    disabled={!connected}
                    className="flex-1 px-4 py-2.5 rounded-xl text-sm outline-none"
                    style={{
                      background: "rgba(255,255,255,0.06)",
                      border: "1px solid rgba(255,255,255,0.1)",
                      color: "#fff",
                    }}
                    onFocus={(e) => (e.target.style.borderColor = "rgba(212,175,55,0.4)")}
                    onBlur={(e) => (e.target.style.borderColor = "rgba(255,255,255,0.1)")}
                  />
                  <button
                    type="submit"
                    data-testid="support-admin-send-btn"
                    disabled={!text.trim() || !connected}
                    className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 transition-all"
                    style={{
                      background: text.trim() && connected ? "#D4AF37" : "rgba(255,255,255,0.06)",
                      color: text.trim() && connected ? "#000" : "#52525B",
                    }}
                  >
                    <PaperPlaneRight className="w-4 h-4" weight="fill" />
                  </button>
                </form>
              </div>
            </>
          )}
        </div>
      </div>
      </AdminSection>
    </div>
  );
}
